import type { Puzzle, PuzzleView, UciMove } from '@shess/schemas'
import { applyMoves, colorOf, fenAfter, isMatingMove, parseUci } from './chess.ts'

/**
 * Lichess puzzle lines interleave: index 0 is the opponent's setup move, then
 * the player and opponent alternate, and the line always ends on a player move.
 * So the player owns the odd indices and there are `length / 2` of them.
 */
export function playerMoveCount(solution: readonly UciMove[]): number {
  return Math.floor(solution.length / 2)
}

/** Solution index of the player's `n`th move, zero-based. */
export function playerMoveIndex(n: number): number {
  return 2 * n + 1
}

/** Strips the solution and precomputes what the board needs to render. */
export function presentPuzzle(puzzle: Puzzle): PuzzleView {
  const setupMove = puzzle.solution[0]!
  const board = applyMoves(puzzle.fen, [setupMove])
  return {
    id: puzzle.id,
    fen: puzzle.fen,
    setupMove,
    positionFen: board.fen(),
    playerColor: colorOf(board.turn()),
    rating: puzzle.rating,
    themes: puzzle.themes,
    playerMoveCount: playerMoveCount(puzzle.solution),
  }
}

/**
 * The position the player faces after having already made `movesMade` correct
 * moves — i.e. with the opponent's replies applied.
 */
export function positionAfter(
  puzzle: Pick<Puzzle, 'fen' | 'solution'>,
  movesMade: number,
): string {
  return fenAfter(puzzle.fen, puzzle.solution.slice(0, playerMoveIndex(movesMade)))
}

export type SolveStep =
  | { status: 'correct'; reply: UciMove; movesRemaining: number }
  | { status: 'solved' }
  | { status: 'incorrect'; expected: UciMove }

/**
 * Judges one submitted move. `movesMade` is how many correct player moves came
 * before this one.
 *
 * An alternate move that delivers checkmate counts, matching Lichess: if the
 * intended move mates, any other mate is just as good an answer.
 */
export function evaluateMove(
  puzzle: Pick<Puzzle, 'fen' | 'solution'>,
  movesMade: number,
  move: UciMove,
): SolveStep {
  const index = playerMoveIndex(movesMade)
  const expected = puzzle.solution[index]
  if (expected === undefined) {
    throw new Error(`puzzle has no player move at index ${index}`)
  }

  if (!movesMatch(expected, move)) {
    const fenBefore = positionAfter(puzzle, movesMade)
    const alternateMate =
      isMatingMove(fenBefore, expected) && isMatingMove(fenBefore, move)
    if (!alternateMate) return { status: 'incorrect', expected }
    return { status: 'solved' }
  }

  const reply = puzzle.solution[index + 1]
  if (reply === undefined) return { status: 'solved' }

  return {
    status: 'correct',
    reply,
    movesRemaining: playerMoveCount(puzzle.solution) - (movesMade + 1),
  }
}

/**
 * UCI equality that tolerates a missing promotion suffix, since some clients
 * omit it when only one promotion is sensible.
 */
function movesMatch(expected: UciMove, actual: UciMove): boolean {
  if (expected === actual) return true
  const a = parseUci(expected)
  const b = parseUci(actual)
  return a.from === b.from && a.to === b.to && (a.promotion ?? 'q') === (b.promotion ?? 'q')
}

/** The square the player should be looking at. */
export function hintSquare(
  puzzle: Pick<Puzzle, 'fen' | 'solution'>,
  movesMade: number,
): string {
  const expected = puzzle.solution[playerMoveIndex(movesMade)]
  if (expected === undefined) throw new Error('no move left to hint at')
  return parseUci(expected).from
}
